
import { computed, defineComponent, nextTick, reactive, ref } from 'vue';
import { useCustomer } from '@/compositions/useCustomer';
import { useRank } from '@/compositions/useRank';
import { usePoint } from '@/compositions/usePoint';
import { useCoupon } from '@/compositions/useCoupon';
import { useChannel } from '@/compositions/useChannel';
import PointInfo from '@/components/organisms/point/PointInfo.vue';
import PointInfoExpire from '@/components/organisms/point/PointInfoExpire.vue';
import VueQrcode from 'vue-qrcode';
import SVGProduct from '@/assets/product.svg?data';
import DialogQRCodeReader from '@/components/organisms/DialogQRCodeReader.vue';
import ComsbiSaasScheme from '@comsbi/common/src/libs/comsbi-saas-scheme';
import Header from '@/components/organisms/Header.vue';
import { useTitle } from '@/compositions/useTitle';
import DialogConfirm from '@/components/molecules/DialogConfirm.vue';
import { useRoute, useRouter } from 'vue-router/composables';
import { ToastProgrammatic as Toast } from 'buefy';
import { i18n } from '@/plugins/i18n';
import { objectFromEntries } from '@/utils';
import SimpleModal from '@/components/molecules/SimpleModal.vue';
import RankCard from '@/components/organisms/rank/RankCard.vue';
import RubyText from '@/components/atoms/RubyText.vue';
import QrCodeLegalNotice from '@/components/atoms/QrCodeLegalNotice.vue';

import liff from '@line/liff';
import TroubleshootingModal from '@/components/molecules/TroubleshootingModal.vue';

const LIFF_ID = process.env.NODE_ENV === 'local' ? window.LIFF_ID || process.env.VUE_APP_LIFF_ID : window.LIFF_ID;

const CHANNEL_ID =
  process.env.NODE_ENV === 'local' ? window.CHANNEL_ID || process.env.VUE_APP_CHANNEL_ID : window.CHANNEL_ID;

export default defineComponent({
  name: 'PagesHome',

  components: {
    Header,
    PointInfo,
    PointInfoExpire,
    VueQrcode,
    DialogQRCodeReader,
    DialogConfirm,
    SimpleModal,
    RankCard,
    RubyText,
    QrCodeLegalNotice,
    TroubleshootingModal,
  },

  setup(_) {
    useTitle(i18n.t('title.customerCard') as string);

    const { get: getChannelById } = useChannel();
    const { get: getCustomer } = useCustomer();
    const { enabled: pointEnabled, get: getPoint } = usePoint();
    const { getCouponProviderById } = useCoupon();
    const { get: getRankById, findRank } = useRank();
    const route = useRoute();
    const router = useRouter();
    const isLoading = ref(false);

    const refDialogQRCodeReader = ref<InstanceType<typeof DialogQRCodeReader>>();
    const dialogConfirmRef = ref<InstanceType<typeof DialogConfirm>>();

    const state = reactive<{
      channel: any /** Channel | null */;
    }>({
      channel: null,
    });

    const qrOption = computed(() => {
      if (!state.channel || !state.channel.attrs.retail || !state.channel.attrs.retail.common) return false;
      return state.channel.attrs.retail.common.qr;
    });

    const customer = ref<{ id: string }>();
    const qrCodeValue = computed(() => {
      if (!customer.value?.id) return '';

      // CS-9157 会員QRコード内容オプション
      if (qrOption.value && qrOption.value.valueOptionEnabled) {
        return customer.value?.id;
      }

      // 会員QRコード内容
      return JSON.stringify({
        type: 'customer',
        customerId: customer.value.id,
        channelId: CHANNEL_ID,
      });
    });

    const currentPoint = ref<number | null>(null);
    const expiringPoint = ref<number | null>(null);
    // const validityPeriod = computed(() => `1年`); // TODO

    const isRuby = ref(false);
    const showHelpModal = ref(false);

    // 店舗アプリ利用設定
    const _SHOP_APP_UNFRIENDLY =
      process.env.NODE_ENV === 'local'
        ? window.SHOP_APP_UNFRIENDLY || process.env.VUE_APP_SHOP_APP_UNFRIENDLY
        : window.SHOP_APP_UNFRIENDLY;
    // string → boolean変換
    const shopAppFriendly = computed(() => (_SHOP_APP_UNFRIENDLY === 'true' ? true : false));

    // 会員証QR表示設定
    const _MEMBERSHIP_QR_DISABLED =
      process.env.NODE_ENV === 'local'
        ? window.MEMBERSHIP_QR_DISABLED || process.env.VUE_APP_MEMBERSHIP_QR_DISABLED
        : window.MEMBERSHIP_QR_DISABLED;
    // string → boolean変換
    const membershipQRDisabled = computed(() => (_MEMBERSHIP_QR_DISABLED === 'true' ? true : false));

    // CS-8567 会員ランク
    const rank = ref<any /** Rank */ | null>(null);

    /**
     * 会員ランクの色設定
     */
    const rankColor = computed(() => {
      if (!rank.value) return null;
      const textColor: string = rank.value.settings.retail.textColor || '';
      const backgroundColor: string = rank.value.settings.retail.backgroundColor || '';
      if (!textColor || !backgroundColor) return null; // 両方設定がないと無効

      return {
        textColor,
        backgroundColor,
      };
    });

    const parseQRValue = async (value: string) => {
      if (!value) return;
      if (value.indexOf('http') === 0) {
        // http
        // TODO 普通のwebアクセス許容する？
      }
      if (value.indexOf('cs:') !== 0) {
        // 無効なコード
        console.log('invalid value. value:' + value);

        Toast.open({
          message: 'QRコードの読み取りに失敗しました。',
          type: 'is-warning',
          position: 'is-top',
        });
        return;
      }

      const [modelKey, modelId, token] = ComsbiSaasScheme.parse(value);
      const [_, queryStr = ''] = value.split('?');
      const query = objectFromEntries(
        queryStr
          .split('&')
          .filter(v => Boolean(v))
          .map(v => v.split('='))
      );
      console.log(modelKey, modelId, query);

      let qrValueType = '';
      switch (modelKey) {
        case ComsbiSaasScheme.POINT_ACTION:
          qrValueType = `${i18n.t('keyword.point')}を`;
          break;
        case ComsbiSaasScheme.COUPON_PROVIDER:
          qrValueType = `${i18n.t('keyword.coupon')}を`;
          break;
        default:
          break;
      }

      // CS-9391 ポイント付与以外のQRコードは無効
      if (qrValueType === '') {
        Toast.open({
          message: '対応していないQRコードです。',
          type: 'is-warning',
          position: 'is-top',
        });
        return;
      }

      let isInstantwin = false;
      // 抽選クーポンかどうかの判別
      if (modelKey === ComsbiSaasScheme.COUPON_PROVIDER) {
        const couponProvider: any = await getCouponProviderById(modelId);
        const instantwin: object = JSON.parse(couponProvider.attrs).instantwin;
        if (instantwin) isInstantwin = true;
      }

      if (!isInstantwin) {
        const confirmed = await dialogConfirmRef.value?.open(`QRから取得`, `${qrValueType}取得します。`, {
          color: 'primary',
          okBtnColor: 'primary',
          width: 320,
          hideCancel: false,
        });
        if (!confirmed) return;
      }

      switch (modelKey) {
        case ComsbiSaasScheme.POINT_ACTION: {
          // ポイント付与
          router.replace(
            `/point_action?id=${modelId}&token=${token}` + (query.shop_ch ? `&shop_ch=${query.shop_ch}` : '')
          ); // ブラウザバックされたくないのでreplace
          break;
        }
        case ComsbiSaasScheme.COUPON_PROVIDER: {
          // クーポン付与
          router.replace(`/coupon_action?id=${modelId}&token=${token}`); // ブラウザバックされたくないのでreplace
          break;
        }
      }
    };

    const startQRCodeReader = async () => {
      // CS-4842 liffのQRリーダー試したがメリットなし。やめる
      // try {
      //   const result = await liff.scanCodeV2();
      //   if (result.value) parseQRValue(result.value!);
      // } catch (e) {
      //   refDialogQRCodeReader.value?.open();
      // }
      refDialogQRCodeReader.value?.open();
    };

    // useAsync
    (async () => {
      try {
        isLoading.value = true;
        // Customer, Point情報取得
        const [_customer, { total, endOfMonthTotal }] = await Promise.all([getCustomer(), getPoint()]);
        customer.value = _customer;
        currentPoint.value = total;
        expiringPoint.value = endOfMonthTotal;

        // CS-9157
        state.channel = await getChannelById(CHANNEL_ID);

        // CS-8567 会員ランク
        if (_customer.attrs && Object.keys(_customer.attrs).length) {
          const customerRankAttr = await findRank(_customer.attrs);
          if (customerRankAttr) {
            const rankModel = await getRankById(customerRankAttr.origin.key);
            rank.value = rankModel;
          }
        }
      } catch (e) {
        console.error(e);
        throw new Error(`顧客情報の解決に失敗しました: ${JSON.stringify(e)}`);
      } finally {
        isLoading.value = false;
      }
    })();

    // CS-5480 外部からQRリーダー起動
    if (route.query.qr) {
      nextTick(() => {
        refDialogQRCodeReader.value?.open();
      });
    }

    return {
      state,
      isLoading,
      refDialogQRCodeReader,
      dialogConfirmRef,
      pointEnabled,
      customer,
      qrCodeValue,
      currentPoint,
      expiringPoint,
      cdn: process.env.VUE_APP_CDN,
      shopAppFriendly,
      membershipQRDisabled,
      // validityPeriod,
      startQRCodeReader,
      parseQRValue,

      isRuby,

      // rank
      rank,
      rankColorStyle: computed(() => {
        // TODO: 汎用的色変え基盤。
        // 現在、色の種類はデフォルトとランク色の2つだが、「管理画面で色を自由に変更したい」という要望アリ
        if (!rankColor.value) return {};
        return {
          color: rankColor.value.textColor,
          backgroundColor: rankColor.value.backgroundColor,
        };
      }),

      svg: {
        product: SVGProduct,
      },

      // DEBUG
      storage: computed(() => ({ localStorage, sessionStorage })),
      clearLiffStore: () => {
        const storage = liff.isInClient() ? sessionStorage : localStorage;
        const keys: string[] = [];
        for (let i = 0; i < storage.length; i++)
          if (storage.key(i)?.indexOf(`LIFF_STORE:${LIFF_ID}`) === 0) keys.push(storage.key(i)!);
        for (const key of keys) storage.removeItem(key);
      },
      expireIDToken: () => {
        const storage = liff.isInClient() ? sessionStorage : localStorage;
        const idTokenSplit = JSON.parse(storage[`LIFF_STORE:${LIFF_ID}:IDToken`]).split('.');
        const dat = JSON.parse(atob(idTokenSplit[1]));
        dat.exp = dat.iat;
        idTokenSplit[0] = btoa(JSON.stringify(dat));
        storage[`LIFF_STORE:${LIFF_ID}:IDToken`] = JSON.stringify(idTokenSplit.join('.'));
      },
    };
  },
});
